<template>
  <v-card elevation="4">
    <v-card-title class="d-flex justify-space-between">
      <span>{{ $t('fleet_health_ui.fleet_health_overview')}}</span>
      <info-modal width="800">
        <v-card-title>
          <h4>{{ $t('fleet_health_ui.fleet_health_overview') }}</h4>
        </v-card-title>
        <v-card-text v-html="infoText"/>
      </info-modal>
    </v-card-title>
    <v-card-text v-if="!loading">
      <div class="d-flex align-center justify-center">
        <v-icon v-if="healthStyle === 'success'" color="success" size="80">mdi-check-bold</v-icon>
        <v-icon v-else :color="healthStyle" size="80">mdi-alert</v-icon>
        <span class="health-percent" :class="`${healthStyle}--text`">{{ percentHealthy }}%</span>
      </div>
      <div class="d-flex justify-center">
        <div class="d-flex flex-column align-end">
          <div class="health-text">{{ $t('healthy') }} : {{ numHealthy }}</div>
          <div class="health-text">{{ $t('unhealthy') }} : {{ numUnhealthy }}</div>
        </div>
      </div>
    </v-card-text>
    <v-card-text v-else>
      <div class="d-flex flex-column align-center">
        <v-skeleton-loader class="text-center mt-3" type="image" width="200" height="65"/>
        <v-skeleton-loader class="text-center mt-4" type="image" width="200" height="20"/>
        <v-skeleton-loader class="text-center mt-3" type="image" width="200" height="20"/>
      </div>
    </v-card-text>
  </v-card>
</template>

<script>
import { get, has, reduce, each, filter, size } from 'lodash'
import InfoModal from '@/components/shared/InfoModal'
import MathUtil from '@/util/MathUtil'
import RouteViolationRecord from '@/orm/models/RouteViolationRecord'
import CurrentUserMixin from '@/components/mixins/CurrentUserMixin'
import TemperatureMixin from '@/components/mixins/TemperatureMixin'

export default {
  name: 'FleetHealthOverview',
  components: {
    InfoModal
  },
  mixins: [
    CurrentUserMixin,
    TemperatureMixin
  ],
  props: {
    loading: {
      type: Boolean,
      default: true
    },
    fleetHealthData: {
      type: Object,
      default: () => {}
    }
  },
  computed: {
    /**
     * Helper function to create the modal text used in the info modal.
     * @returns {*}
     */
    infoText () {
      return this.$t('fleet_health_ui.modal_info.fleet_health_overview', {
        coolerNominalTemp: this.temperatureToCustomerScale(RouteViolationRecord.DEFAULT_NOMINAL_COOLER_TEMPERATURE),
        freezerNominalTemp: this.temperatureToCustomerScale(RouteViolationRecord.DEFAULT_NOMINAL_FREEZER_TEMPERATURE),
        tempFormat: this.customerContext.tempFormat()
      })
    },
    percentHealthy () {
      return MathUtil.percentage(this.numHealthy, this.numHealthy + this.numUnhealthy, 0)
    },
    numHealthy () {
      return size(filter(this.vehicleHealthStats, x => x === 'healthy'))
    },
    numUnhealthy () {
      return size(filter(this.vehicleHealthStats, x => x === 'unhealthy'))
    },
    /**
     * Reduce vehicles across all customers to a map of their status value, keyed by vehicle id.
     * This will remove duplicate vehicles (vehicles that are shared among more than one of the available customers.)
     * If the vehicle is unhealthy for any customer, then it is considered unhealthy overall.
     */
    vehicleHealthStats () {
      return reduce(this.fleetHealthData, (resultMap, customer) => {
        each(get(customer, 'healthy'), vehicle => {
          const vehicleId = get(vehicle, 'vehicle_id')
          if (!has(resultMap, vehicleId)) {
            resultMap[vehicleId] = 'healthy'
          }
        })
        each(get(customer, 'unhealthy'), vehicle => {
          resultMap[get(vehicle, 'vehicle_id')] = 'unhealthy'
        })
        return resultMap
      }, {})
    },
    /**
     * Get the style to use for the vehicle display, depending on the percentage of healthy vehicles.
     * @returns {string}
     */
    healthStyle () {
      if (this.percentHealthy > 75) {
        return 'success'
      }
      if (this.percentHealthy > 50) {
        return 'warning'
      }
      return 'danger'
    }
  }
}
</script>

<style scoped lang="scss">

.health-percent {
  font-family: "Inter Bold", sans-serif;
  font-size: 65px;
}
.health-text {
  font-size: 24px;
  line-height: 1.5;
}

</style>
